import dateUtils from '../../../Utils/dateUtils';
import { versionStatusFromName, versionStatusName } from '../../../Constants/VOSS/vossVersionStatus';
import { VossScheduledChangeStatus } from '../../../Constants/VOSS/vossScheduledChangeStatus';
import type { VossVersionStatus } from '../../../Constants/VOSS/vossVersionStatus';
import type { PackageScheduledChange, PackageScheduledChangeRequest, PackageScheduledChangeTarget, ScheduledChangeDraft } from './Packages.types';

// VOSS caps the note at 500 characters (VSCO-3780)
export const SCHEDULED_CHANGE_NOTE_MAX_LENGTH = 500;

type VersionStatusName = NonNullable<PackageScheduledChangeTarget['status']>;

export const isPendingScheduledChange = (change: PackageScheduledChange): boolean => change.status === VossScheduledChangeStatus.Pending;

export const emptyScheduledChangeDraft = (targets: VossVersionStatus[]): ScheduledChangeDraft => ({
    targetStatus: targets[0] ?? null,
    scheduledDate: '',
    name: '',
    note: '',
});

export const toScheduledChangeDraft = (change: PackageScheduledChange): ScheduledChangeDraft => ({
    targetStatus: versionStatusFromName(change.package?.status) ?? null,
    scheduledDate: change.scheduledDate === undefined ? '' : dateUtils.formatDate(change.scheduledDate),
    name: change.package?.name ?? '',
    note: change.note ?? '',
});

const blankToNull = (value: string): string | null => (value.trim().length === 0 ? null : value.trim());

export const toScheduledChangeRequest = (draft: ScheduledChangeDraft): PackageScheduledChangeRequest => ({
    // a null name leaves the package name unchanged; VOSS treats null and omitted alike
    package: { name: blankToNull(draft.name), status: draft.targetStatus === null ? null : (versionStatusName(draft.targetStatus) as VersionStatusName) },
    // local midnight without an offset: VOSS executes the change at the start of that day
    scheduledDate: `${draft.scheduledDate}T00:00:00`,
    note: blankToNull(draft.note),
});

const DATE_INPUT = /^\d{4}-\d{2}-\d{2}$/;

// the picker emits YYYY-MM-DD, so plain string comparison orders dates correctly; a saved change may keep its own date
// even once that day has come, so its note can still be edited
export const isSchedulableDate = (date: string, today: string, original?: string): boolean => DATE_INPUT.test(date) && (date > today || date === original);

export interface ScheduledChangeDraftErrors {
    targetStatus: boolean;
    scheduledDate: boolean;
    note: boolean;
}

export const scheduledChangeDraftErrors = (draft: ScheduledChangeDraft, today: string, originalDate?: string): ScheduledChangeDraftErrors => ({
    targetStatus: draft.targetStatus === null,
    scheduledDate: !isSchedulableDate(draft.scheduledDate, today, originalDate),
    note: draft.note.length > SCHEDULED_CHANGE_NOTE_MAX_LENGTH,
});

export const isValidScheduledChangeDraft = (draft: ScheduledChangeDraft, today: string, originalDate?: string): boolean =>
    !Object.values(scheduledChangeDraftErrors(draft, today, originalDate)).some(Boolean);

// pending changes first with the soonest on top; executed and cancelled ones follow as history, newest first
export const sortScheduledChanges = (changes: PackageScheduledChange[]): PackageScheduledChange[] => {
    const time = (change: PackageScheduledChange) => (change.scheduledDate === undefined ? 0 : new Date(change.scheduledDate).getTime());
    const pending = changes.filter(isPendingScheduledChange).sort((a, b) => time(a) - time(b));
    const history = changes.filter((change) => !isPendingScheduledChange(change)).sort((a, b) => time(b) - time(a));

    return [...pending, ...history];
};
